import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, getDocs, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import productionCostService from '../../services/productionCostService';

const AccessoryManagement = () => {
  const { userData } = useAuth();
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, procured

  useEffect(() => {
    if (!userData?.companyId) return;

    // We fetch all production orders and extract unique accessories
    const q = query(collection(db, 'companies', userData.companyId, 'productionOrders'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allAccs = [];
      snapshot.docs.forEach(orderDoc => {
        const orderData = orderDoc.data();
        if (orderData.accessories) {
          orderData.accessories.forEach((acc, index) => {
            allAccs.push({
              ...acc,
              orderId: orderDoc.id,
              orderName: orderData.id || orderDoc.id.substring(0, 8),
              productName: orderData.product,
              originalIndex: index
            });
          });
        }
      });
      
      // Sort by status and date
      setAccessories(allAccs.sort((a, b) => {
        if (a.status === 'Tedarik Edildi' && b.status !== 'Tedarik Edildi') return 1;
        if (a.status !== 'Tedarik Edildi' && b.status === 'Tedarik Edildi') return -1;
        return 0;
      }));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userData?.companyId]);

  const handleStatusUpdate = async (acc) => {
    if (!userData?.companyId) return;

    try {
      // Update in Firestore
      const orderRef = doc(db, 'companies', userData.companyId, 'productionOrders', acc.orderId);
      
      // Fetch current order to get fresh accessories array
      const currentAccs = (await getDocs(query(collection(db, 'companies', userData.companyId, 'productionOrders'), where('__name__', '==', acc.orderId)))).docs[0].data().accessories;
      
      const updatedAccs = [...currentAccs];
      updatedAccs[acc.originalIndex] = { ...updatedAccs[acc.originalIndex], status: 'Tedarik Edildi' };

      // Log cost to supplier
      await productionCostService.handleAccessoryOrder(userData.companyId, acc.orderId, updatedAccs[acc.originalIndex]);

      await updateDoc(orderRef, {
        accessories: updatedAccs,
        updatedAt: new Date()
      });

      alert(`${acc.productName} aksesuarı için giriş kaydedildi.`);
    } catch (err) {
      console.error("Error updating accessory:", err);
      alert("Hata oluştu: " + err.message);
    }
  };

  const filteredAccessories = accessories.filter(acc => {
    if (filter === 'pending') return acc.status !== 'Tedarik Edildi';
    if (filter === 'procured') return acc.status === 'Tedarik Edildi';
    return true;
  });

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>;

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: '"Inter", sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1a1d29', margin: 0 }}>Aksesuar Yönetimi</h1>
          <p style={{ color: '#64748b', marginTop: '4px' }}>Tüm üretimler için gereken yardımcı malzemelerin takibi</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '10px' }}>
          {['all', 'pending', 'procured'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: filter === f ? 'white' : 'transparent',
                color: filter === f ? '#06C167' : '#64748b',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: filter === f ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              {f === 'all' ? 'Tümü' : (f === 'pending' ? 'Bekleyenler' : 'Tedarik Edilenler')}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ padding: '16px 24px', fontSize: '13px', fontWeight: '700', color: '#64748b' }}>Aksesuar</th>
              <th style={{ padding: '16px 24px', fontSize: '13px', fontWeight: '700', color: '#64748b' }}>Üretim Emri</th>
              <th style={{ padding: '16px 24px', fontSize: '13px', fontWeight: '700', color: '#64748b' }}>Miktar</th>
              <th style={{ padding: '16px 24px', fontSize: '13px', fontWeight: '700', color: '#64748b' }}>Tedarikçi</th>
              <th style={{ padding: '16px 24px', fontSize: '13px', fontWeight: '700', color: '#64748b' }}>Durum</th>
              <th style={{ padding: '16px 24px', fontSize: '13px', fontWeight: '700', color: '#64748b', textAlign: 'right' }}>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filteredAccessories.map((acc, idx) => (
              <tr key={`${acc.orderId}-${idx}`} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }}>
                <td style={{ padding: '16px 24px' }}>
                  <div style={{ fontWeight: '600', color: '#1e293b' }}>{acc.productName}</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>{acc.productName} için aksesuar</div>
                </td>
                <td style={{ padding: '16px 24px' }}>
                  <div style={{ fontSize: '14px', color: '#1e293b' }}>{acc.productName}</div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>#{acc.orderName.toUpperCase()}</div>
                </td>
                <td style={{ padding: '16px 24px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>{acc.requiredQuantity} {acc.unit}</div>
                </td>
                <td style={{ padding: '16px 24px' }}>
                  <span style={{ 
                    fontSize: '13px', 
                    color: '#64748b', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px' 
                  }}>
                    🏢 {acc.supplierName || 'Atanmamış'}
                  </span>
                </td>
                <td style={{ padding: '16px 24px' }}>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '700',
                    background: acc.status === 'Tedarik Edildi' ? '#dcfce7' : '#fee2e2',
                    color: acc.status === 'Tedarik Edildi' ? '#166534' : '#991b1b',
                  }}>
                    {acc.status || 'Beklemede'}
                  </span>
                </td>
                <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                  <button
                    disabled={acc.status === 'Tedarik Edildi'}
                    onClick={() => handleStatusUpdate(acc)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      background: acc.status === 'Tedarik Edildi' ? '#f1f5f9' : '#06C167',
                      color: acc.status === 'Tedarik Edildi' ? '#94a3b8' : 'white',
                      fontWeight: '600',
                      fontSize: '12px',
                      cursor: acc.status === 'Tedarik Edildi' ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {acc.status === 'Tedarik Edildi' ? 'Tedarik Edildi ✓' : 'Tedarik Girişi Yap'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredAccessories.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
            Aksesuar kaydı bulunamadı.
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessoryManagement;
